import { type FormEvent, useEffect, useState } from 'react'
import { Head, Link, router, useForm, usePage } from '@inertiajs/react'
import { Pencil, Trash2, X } from 'lucide-react'
import type { CareersScanProps, DraftedPosition, PositionRow, Team, WorkspaceSetup } from '@/types/company'
import type { SharedPageProps } from '@/types/shared'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { CompanyHeader } from '@/components/Shared/CompanyHeader'
import { CompanyWorkspaceNav } from '@/components/Shared/CompanyWorkspaceNav'
import { ConfirmDeleteDialog } from '@/components/Shared/ConfirmDeleteDialog'
import { InlineErrorBanner } from '@/components/Shared/InlineErrorBanner'
import { ScanningIndicator } from '@/components/Shared/ScanningIndicator'
import { TeamCombobox } from '@/components/Shared/TeamCombobox'
import { WorkspaceSetupWizard } from '@/components/Shared/WorkspaceSetupWizard'
import { useScanPolling } from '@/hooks/useScanPolling'

interface IndexProps {
  positions: PositionRow[]
  teams: Team[]
  activeTeamId: number | null
  workspaceSetup: WorkspaceSetup
  careersScan: CareersScanProps
  draftedPositions: DraftedPosition[]
}

// Type alias (not interface): usePage<T>'s PageProps constraint needs the
// implicit index signature only object-literal types carry.
type IndexPageProps = SharedPageProps & {
  positions: PositionRow[]
  teams: Team[]
  activeTeamId: number | null
  errors?: Record<string, string>
}

interface DraftEdit {
  title: string
  location: string
  teamName: string
}

function seedDrafts(draftedPositions: DraftedPosition[]): Record<number, DraftEdit> {
  return Object.fromEntries(
    draftedPositions.map((draft) => [
      draft.id,
      { title: draft.title, location: draft.location ?? '', teamName: draft.teamName ?? '' },
    ]),
  )
}

export default function CompanyPositionsIndex({
  positions,
  teams,
  activeTeamId,
  workspaceSetup,
  careersScan,
  draftedPositions,
}: IndexProps) {
  const { errors: pageErrors } = usePage<IndexPageProps>().props
  const form = useForm({ title: '', location: '', team_name: '' })
  const [dialogOpen, setDialogOpen] = useState(false)
  const activeTeam = teams.find((team) => team.id === activeTeamId) ?? null
  const [mountedAtMs] = useState(() => Date.now())
  const setupComplete = workspaceSetup.profileConfirmed && workspaceSetup.positionsCount > 0

  const scanning = careersScan.scanState === 'pending'
  const scanStartedAtMs = careersScan.startedAt
    ? new Date(careersScan.startedAt).getTime()
    : mountedAtMs

  const { timedOut } = useScanPolling({
    active: scanning,
    startedAtMs: scanStartedAtMs,
    reloadKeys: ['careersScan', 'draftedPositions', 'positions', 'workspaceSetup'],
  })

  const [drafts, setDrafts] = useState<Record<number, DraftEdit>>(() => seedDrafts(draftedPositions))
  const [approving, setApproving] = useState(false)

  // Re-seed editable state whenever the server list changes (poll reload / partial reload).
  useEffect(() => {
    setDrafts(seedDrafts(draftedPositions))
  }, [draftedPositions])

  function updateDraft(id: number, patch: Partial<DraftEdit>) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }

  const approvableCount = draftedPositions.filter((draft) => {
    const edit = drafts[draft.id]
    return edit && edit.title.trim() !== '' && edit.location.trim() !== ''
  }).length

  function approveAll() {
    const rows = draftedPositions.map((draft) => ({
      drafted_position_id: draft.id,
      title: drafts[draft.id]?.title ?? '',
      location: drafts[draft.id]?.location ?? '',
      team_name: drafts[draft.id]?.teamName ?? '',
    }))
    router.post(
      '/company/drafted_positions/accept_all',
      { rows },
      {
        preserveScroll: true,
        onStart: () => setApproving(true),
        onFinish: () => setApproving(false),
      },
    )
  }

  function discardDraft(id: number) {
    router.post(`/company/drafted_positions/${id}/discard`, {}, { preserveScroll: true })
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    form.post('/company/positions', {
      onSuccess: () => {
        form.reset()
        setDialogOpen(false)
      },
    })
  }

  return (
    <>
      <Head title="Positions — Hyrin" />

      <div className="min-h-screen bg-muted">
        <CompanyHeader />

        <main
          className={`mx-auto px-6 py-[52px] ${setupComplete ? 'max-w-[1120px]' : 'max-w-[980px]'}`}
        >
          <p className="mb-[14px] font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            Company workspace
          </p>
          <h1 className="mb-[10px] font-heading text-[36px] font-bold leading-[1.06] tracking-[-0.025em] text-foreground">
            Positions
          </h1>

          {setupComplete && <CompanyWorkspaceNav active="positions" />}

          <p className="mb-7 max-w-[640px] font-sans text-base leading-[1.55] text-muted-foreground">
            Open roles at your company. Attach a team to group them, or leave them company-wide.
          </p>

          {!setupComplete && (
            <div className="mb-8">
              <WorkspaceSetupWizard setup={workspaceSetup} />
            </div>
          )}

          <div className="flex flex-col gap-4">
              {scanning && !timedOut && (
                <div className="self-start rounded-card border border-border bg-background p-6 shadow-sm">
                  <ScanningIndicator
                    startedAtMs={scanStartedAtMs}
                    label="Scanning your careers page"
                  />
                  <p className="mt-3 max-w-[420px] font-sans text-[14px] leading-[1.55] text-muted-foreground">
                    We're reading your careers page to suggest positions. Results will appear below as
                    they come in.
                  </p>
                </div>
              )}

              {draftedPositions.length > 0 && (
                <div className="rounded-card border border-border bg-background p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                      {draftedPositions.length} suggested{' '}
                      {draftedPositions.length === 1 ? 'position' : 'positions'} from your careers page
                    </p>
                    <Button type="button" onClick={approveAll} disabled={approving || approvableCount === 0}>
                      {approving
                        ? 'Approving…'
                        : `Approve ${approvableCount} ${approvableCount === 1 ? 'position' : 'positions'}`}
                    </Button>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="pb-2 text-left font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                          Title
                        </th>
                        <th className="pb-2 text-left font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                          Location
                        </th>
                        <th className="pb-2 text-left font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                          Team
                        </th>
                        <th className="pb-2 text-right font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                          <span className="sr-only">Remove</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {draftedPositions.map((draft) => {
                        const edit = drafts[draft.id] ?? { title: '', location: '', teamName: '' }
                        const needsLocation = edit.location.trim() === ''
                        return (
                          <tr key={draft.id} className="align-top">
                            <td className="py-2 pr-2">
                              <Input
                                type="text"
                                value={edit.title}
                                onChange={(e) => updateDraft(draft.id, { title: e.target.value })}
                                disabled={approving}
                              />
                              {draft.possibleDuplicate && (
                                <span className="mt-1 inline-flex items-center rounded-full border border-border px-2 py-[3px] font-mono text-[10px] uppercase tracking-eyebrow text-muted-foreground">
                                  Possible duplicate
                                </span>
                              )}
                            </td>
                            <td className="py-2 pr-2">
                              <Input
                                type="text"
                                value={edit.location}
                                onChange={(e) => updateDraft(draft.id, { location: e.target.value })}
                                placeholder="Remote"
                                disabled={approving}
                                aria-invalid={needsLocation || undefined}
                                className={
                                  needsLocation
                                    ? 'border-destructive focus-visible:ring-destructive'
                                    : undefined
                                }
                              />
                              {needsLocation && (
                                <p className="mt-1 font-sans text-[12px] text-destructive">
                                  Add a location
                                </p>
                              )}
                            </td>
                            <td className="py-2 pr-2">
                              <TeamCombobox
                                value={edit.teamName}
                                teams={teams}
                                disabled={approving}
                                onChange={(value) => updateDraft(draft.id, { teamName: value })}
                                listId={`draft-team-${draft.id}`}
                              />
                            </td>
                            <td className="py-2 text-right">
                              <button
                                type="button"
                                aria-label="Remove suggestion"
                                onClick={() => discardDraft(draft.id)}
                                disabled={approving}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <X className="size-[16px]" />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

            <div className="rounded-card bg-background p-8 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                    {positions.length} {positions.length === 1 ? 'position' : 'positions'}
                  </span>
                  {activeTeam && (
                    <>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        in {activeTeam.name}
                      </span>
                      <Link
                        href="/company/positions"
                        className="font-sans text-[13px] text-primary hover:underline"
                      >
                        Clear
                      </Link>
                    </>
                  )}
                </div>
                <Button type="button" onClick={() => setDialogOpen(true)}>
                  New position
                </Button>
              </div>

              {positions.length === 0 ? (
                <p className="font-sans text-[14px] text-muted-foreground">
                  No positions yet. Create your first position to get started.
                </p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="w-[36%] pb-3 text-left font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        Position
                      </th>
                      <th className="w-[26%] pb-3 text-left font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        Location
                      </th>
                      <th className="w-[26%] pb-3 text-left font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        Team
                      </th>
                      <th className="w-[12%] whitespace-nowrap pb-3 pl-6 text-right font-mono text-[11px] font-medium uppercase tracking-eyebrow text-muted-foreground">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {positions.map((position) => (
                      <tr key={position.id}>
                        <td className="py-3 font-sans text-[14px] font-medium">
                          <Link
                            href={`/company/funnel?position_id=${position.id}`}
                            className="text-foreground hover:text-primary"
                          >
                            {position.title}
                          </Link>
                        </td>
                        <td className="py-3 font-sans text-[14px] text-muted-foreground">
                          {position.location}
                        </td>
                        <td className="py-3 font-sans text-[14px]">
                          {position.team ? (
                            <Link
                              href={`/company/funnel?team_id=${position.team.id}`}
                              className="text-foreground hover:text-primary"
                            >
                              {position.team.name}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">Company-wide</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-3 pl-6 text-right">
                          <span className="inline-flex items-center gap-4">
                            <Link
                              href={`/company/positions/${position.id}`}
                              aria-label="Edit position"
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="size-[16px]" />
                            </Link>
                            <ConfirmDeleteDialog
                              trigger={
                                <button
                                  type="button"
                                  aria-label="Delete position"
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  <Trash2 className="size-[16px]" />
                                </button>
                              }
                              title="Delete position?"
                              description={`This removes “${position.title}” and its pipeline candidates. This can't be undone.`}
                              onConfirm={() => router.delete(`/company/positions/${position.id}`)}
                            />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a position</DialogTitle>
                <DialogDescription>
                  Open a new role and attach it to a team, or leave it company-wide.
                </DialogDescription>
              </DialogHeader>

              <InlineErrorBanner message={pageErrors?.base} />

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="flex flex-col gap-[6px]">
                  <label
                    htmlFor="position-title"
                    className="font-sans text-[13px] font-medium text-foreground"
                  >
                    Title
                  </label>
                  <Input
                    id="position-title"
                    type="text"
                    value={form.data.title}
                    onChange={(e) => form.setData('title', e.target.value)}
                    placeholder="Software Engineer"
                    disabled={form.processing}
                  />
                  {form.errors.title && (
                    <p className="font-sans text-[12px] text-destructive">{form.errors.title}</p>
                  )}
                </div>

                <div className="flex flex-col gap-[6px]">
                  <label
                    htmlFor="position-location"
                    className="font-sans text-[13px] font-medium text-foreground"
                  >
                    Location
                  </label>
                  <Input
                    id="position-location"
                    type="text"
                    value={form.data.location}
                    onChange={(e) => form.setData('location', e.target.value)}
                    placeholder="Remote"
                    disabled={form.processing}
                  />
                  {form.errors.location && (
                    <p className="font-sans text-[12px] text-destructive">
                      {form.errors.location}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-[6px]">
                  <label
                    htmlFor="position-team"
                    className="font-sans text-[13px] font-medium text-foreground"
                  >
                    Team
                  </label>
                  <TeamCombobox
                    id="position-team"
                    value={form.data.team_name}
                    teams={teams}
                    disabled={form.processing}
                    onChange={(value) => form.setData('team_name', value)}
                    listId="new-position-team"
                  />
                </div>

                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline" disabled={form.processing}>
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button type="submit" disabled={form.processing}>
                    {form.processing ? 'Creating…' : 'Create position'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </>
  )
}
